import React from 'react';
import { shortParts } from '../../services/findingText';

// The pieces of the review panel's results view around the active finding:
// header with run details, category tabs, the other findings' rows, the
// Dismiss / Apply bar and the footer.

const RUN_LABELS = { proofread: 'Proofread', review: 'Review' };

function checkedAgo(createdAt) {
    if (!createdAt) return '';
    const seconds = Math.max(0, Math.floor(Date.now() / 1000) - createdAt);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
    return new Date(createdAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Removed text right before its replacement ("reached" → "reach") gets a
// space between them so the two don't read as one word.
const touches = (prev, part) => prev?.type === 'removed' && part.type === 'added';

// Fix text as diff parts: kept words plain, removed struck through, added
// highlighted; an addition's inserted sentence reads as plain text. Parts
// carry their own spacing.
export function PartsText({ parts, addition }) {
    return parts.map((part, i) => {
        const space = touches(parts[i - 1], part) ? ' ' : '';
        const className = part.type === 'same' ? null
            : part.type === 'removed' ? 'crp-part-removed'
                : addition ? 'crp-part-insert' : 'crp-part-added';
        return (
            <React.Fragment key={i}>
                {space}
                {className ? <span className={className}>{part.text}</span> : part.text}
            </React.Fragment>
        );
    });
}

export function ResultsHeader({ mode, open, runs, infoOpen, onToggleInfo, failed, onRetry, onRunAgain }) {
    return (
        <>
            <div className="crp-head">
                <span className="crp-head-title">{mode === 'proofread' ? 'Proofread' : 'Full review'}</span>
                {open > 0 && <span className="crp-head-open">{open} open</span>}
                <span className="crp-head-actions">
                    <button
                        type="button"
                        className="crp-icon-btn"
                        title={mode === 'proofread' ? 'Proofread again' : 'Run the full review again'}
                        onClick={onRunAgain}
                    >
                        <i className="pi pi-refresh" />
                    </button>
                    <button type="button" className="crp-icon-btn crp-head-info" title="Run details" onClick={onToggleInfo}>
                        <i className="pi pi-info-circle" />
                    </button>
                </span>
            </div>
            {infoOpen && (
                <div className="crp-popover">
                    {runs.map(run => (
                        <div key={run.id} className="crp-popover-row">
                            <span>{RUN_LABELS[run.kind]}</span>
                            <span className="ne-mono crp-popover-model">{run.model_used || '—'}</span>
                        </div>
                    ))}
                    <div className="crp-popover-foot">
                        Checked {checkedAgo(Math.max(...runs.map(run => run.created_at || 0)))} · models are set in Settings
                    </div>
                </div>
            )}
            {failed.map(run => (
                <div key={run.id} className="crp-failed">
                    <i className="pi pi-exclamation-triangle" />
                    <span>{RUN_LABELS[run.kind]} failed on the server.</span>
                    <button type="button" className="crp-link-btn" onClick={onRetry}>Try again</button>
                </div>
            ))}
        </>
    );
}

export function CategoryTabs({ groups, tab, onSelect }) {
    if (groups.length < 2) return <div className="crp-divider" />;
    return (
        <div className="crp-tabs">
            {groups.map(group => {
                const active = group.key === tab;
                const handled = group.findings.length - group.open;
                return (
                    <button
                        key={group.key}
                        type="button"
                        className={`crp-tab${active ? ' crp-tab-active' : ''}`}
                        onClick={() => onSelect(group.key)}
                    >
                        <span className="crp-tab-label">
                            <span className="crp-dot" style={{ background: group.color }} />
                            <span>{group.label}</span>
                            {group.open > 0
                                ? <span className="crp-tab-count">{group.open}</span>
                                : <span className="crp-tab-count crp-tab-count-done">✓</span>}
                        </span>
                        <span className="crp-tab-track" style={active ? { background: `${group.color}33` } : undefined}>
                            <span
                                className="crp-tab-fill"
                                style={{
                                    width: `${Math.round((handled / group.findings.length) * 100)}%`,
                                    background: group.color,
                                    opacity: active ? 1 : 0.5,
                                }}
                            />
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function CollapsedRow({ finding, parts, onSelect }) {
    return (
        <button type="button" className="crp-row" onClick={() => onSelect(finding)}>
            <span className="crp-row-sub" title={finding.subtype}>{finding.subtype}</span>
            <span className="crp-row-text">
                <PartsText parts={shortParts(parts)} addition={finding.category === 'suggestion'} />
            </span>
        </button>
    );
}

export function ResolvedRow({ finding, text }) {
    const applied = finding.resolution === 'applied';
    return (
        <div className="crp-done-row">
            <span className="crp-done-sub" title={finding.subtype}>{finding.subtype}</span>
            <span className="crp-done-text">{text}</span>
            <span className={`crp-done-status${applied ? ' crp-done-applied' : ''}`}>
                {!applied ? 'Dismissed' : finding.category === 'suggestion' ? 'Inserted' : 'Applied'}
            </span>
        </div>
    );
}

export function CategoryDone({ label, next, onNext }) {
    return (
        <div className="crp-cat-done">
            <i className="pi pi-check-circle crp-cat-done-icon" />
            <div className="crp-cat-done-title">{label} done</div>
            {next && (
                <button type="button" className="hk-btn hk-btn-sm crp-cat-done-next" onClick={() => onNext(next.key)}>
                    Next: {next.label} <i className="pi pi-arrow-right" />
                </button>
            )}
        </div>
    );
}

export function ActionBar({ disabled, applyLabel, stale, onDismiss, onApply }) {
    return (
        <div className="crp-actionbar">
            {stale && (
                <span className="crp-stale" title="The card text no longer matches this fix. Edit the card by hand, then mark it fixed.">
                    <i className="pi pi-exclamation-triangle" /> Text has changed
                </span>
            )}
            <button type="button" className="hk-btn crp-action" disabled={disabled} onClick={onDismiss}>
                Dismiss<span className="crp-key">D</span>
            </button>
            <button type="button" className="hk-btn hk-btn-primary crp-action" disabled={disabled} onClick={onApply}>
                {applyLabel}<span className="crp-key">A</span>
            </button>
        </div>
    );
}

export function TriageFooter({ applyAllCount, onApplyAll }) {
    return (
        <div className="crp-footer">
            {applyAllCount > 1 && (
                <button type="button" className="crp-apply-all" onClick={onApplyAll} title="One undo step in the editor">
                    Apply all {applyAllCount} proofread fixes
                </button>
            )}
            <span className="crp-hints">
                <span className="crp-hint-key">↑</span>
                <span className="crp-hint-key">↓</span>
                <span className="crp-hint-label">move</span>
                <span className="crp-hint-key">←</span>
                <span className="crp-hint-key">→</span>
                <span className="crp-hint-label">type</span>
            </span>
        </div>
    );
}
